import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, share, delay } from "rxjs/operators";
import { withErrorHandler } from './error-handler.util';
import { environment } from '../../environments';
import { UserLicense, UserSubscription } from '../interfaces/user';
import { AuthService } from './auth.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class LicenseService {
  baseURL = environment.API_URL + "v2/license/"
  baseShopURL = environment.API_URL + "v2/shop/"

  constructor(private http: HttpClient, private user: UserService) { }

  private static lastLicense: Promise<UserLicense>;

  getLicense(user_id: number | null = null): Promise<UserLicense> {
    if(!user_id && LicenseService.lastLicense != null) {
      return LicenseService.lastLicense;
    }
    const params = {}
    if (user_id) {
      params['user_id'] = user_id
    }
    const l = this.http.get<UserLicense>(this.baseURL + "get-user-license", { params }).pipe(
      withErrorHandler({
        operation: 'getLicense',
        cache: true,
        fallback: {} as UserLicense
      }),
      map((e) => {
        if (e?.expiration) {
          const date = new Date(e.expiration)
          if (!Number.isNaN(date.getTime())) {
            e.expiration = date.toISOString().split('T')[0];
          }
        }
        return e;
      }),
      catchError((error: any) => {
        console.error("[LICENSE SERVICE ERROR] Failed to get license", error);
        // Clear cache on error to prevent returning stale rejected promises
        if (!user_id) {
          LicenseService.lastLicense = null;
        }
        return throwError(() => error);
      }),
      share(),
    ).toPromise();
    if (!user_id) {
      LicenseService.lastLicense = l;
    }
    return l;
  }

  updateLicense(body: { user_id: number; license_type: string; slots: number; expiration: string }): Observable<any> {
    return this.http
      .post(this.baseURL + "add-user-license", body, {
        observe: "response",
      })
      .pipe(
        withErrorHandler({ operation: 'updateLicense', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('updateLicense failed:', err);
          return throwError(() => err);
        }),
        map((e) => {
          if (e.status == 200) {
            return { ok: true };
          } else {
            return { ok: false, msg: e.body["error"] };
          }
        })
      );
  }

  async isFreeLocked(): Promise<boolean> {
    const user = await this.user.getLastUser();
    if(!user?.role?.includes('MASTER')) {
      return false;
    }
    const licenseData = await this.getLicense();
    const license = licenseData.license;
    return !license || license == 'free';
  }

  async isFullLocked(): Promise<boolean> {
    const license = await this.getLicense();
    return license.license != 'full';
  }

  /** Creates a Stripe Checkout session and returns its URL. */
  async startCheckout(payload: { plan: string, recurrence: string, amount: number, success_url: string }): Promise<string> {
    const { url } = await this.http.post<{ url: string }>(this.baseShopURL + "subscribe", payload).toPromise()
    return url
  }

  /** Creates a Stripe billing portal session and returns its URL. */
  async subscriptionPortal(returnUrl: string): Promise<string> {
    const { url } = await this.http.post<{ url: string }>(this.baseShopURL + "edit-subscription", { return_url: returnUrl }).toPromise()
    return url
  }

  private static lastCurrentSub: Promise<UserSubscription>;

  currentSubscription() {
    if(LicenseService.lastCurrentSub != null) {
      return LicenseService.lastCurrentSub;
    }
    const v = this.http.get<UserSubscription>(this.baseShopURL + "license").pipe(
      withErrorHandler({ operation: 'currentSubscription', cache: true, fallback: {} as UserSubscription })
    ).toPromise();
    LicenseService.lastCurrentSub = v;
    return v;
  }

}
